/* ==========================================================================
   Dirty Turf — theme.js
   Vanilla JS: header, cart drawer (AJAX), variant/selling-plan picker,
   quantity steppers, accordion, modal, GA4 helper.
   ========================================================================== */

(function () {
  "use strict";

  document.documentElement.classList.remove("no-js");

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  var qs = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var qsa = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  function formatMoney(cents) {
    var value = (Math.round(cents) / 100).toFixed(2);
    return "$" + value;
  }

  var PROD_HOSTNAME = (window.themeSettings && window.themeSettings.ga4ProductionHost) || "shop.dirtyturf.com";

  function isProductionHost() {
    return window.location.hostname === PROD_HOSTNAME;
  }

  function fireGA4(eventName, params) {
    if (!isProductionHost()) return;
    if (typeof window.gtag !== "function") return;
    window.gtag("event", eventName, params || {});
  }
  window.fireGA4 = fireGA4;

  function fireOpenai(eventName, data, options) {
    if (!isProductionHost()) return;
    if (typeof window.oaiq !== "function") return;
    if (options) window.oaiq("measure", eventName, data, options);
    else window.oaiq("measure", eventName, data);
  }
  window.fireOpenai = fireOpenai;

  /* ------------------------------------------------------------------ *
   * Toast
   * ------------------------------------------------------------------ */

  var toastEl = null;
  var toastTimer = null;
  function showToast(message) {
    if (!toastEl) toastEl = qs("#QuickToast");
    if (!toastEl) return;
    toastEl.textContent = message;
    toastEl.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove("is-visible");
    }, 2200);
  }

  /* ------------------------------------------------------------------ *
   * Header — hide on scroll (mobile), mobile menu toggle
   * ------------------------------------------------------------------ */

  (function headerBehavior() {
    var header = qs(".site-header");
    if (!header) return;
    var toggle = qs(".site-header__mobile-toggle");
    var menu = qs(".site-header__mobile-menu");

    if (toggle && menu) {
      toggle.addEventListener("click", function () {
        var isOpen = menu.classList.toggle("is-open");
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
        toggle.innerHTML = isOpen ? toggle.getAttribute("data-icon-close") : toggle.getAttribute("data-icon-open");
      });
      qsa(".site-header__mobile-link", menu).forEach(function (link) {
        link.addEventListener("click", function () {
          menu.classList.remove("is-open");
          toggle.setAttribute("aria-expanded", "false");
          toggle.innerHTML = toggle.getAttribute("data-icon-open");
        });
      });
    }

    var lastScrollY = window.scrollY || 0;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var currentY = window.scrollY || 0;
        var isDesktop = window.innerWidth >= 1024;

        if (isDesktop) {
          header.classList.remove("is-hidden");
        } else if (currentY < 60) {
          header.classList.remove("is-hidden");
        } else if (currentY - lastScrollY > 5) {
          header.classList.add("is-hidden");
          if (menu) {
            menu.classList.remove("is-open");
            if (toggle) toggle.setAttribute("aria-expanded", "false");
          }
        } else if (lastScrollY - currentY > 5) {
          header.classList.remove("is-hidden");
        }

        lastScrollY = currentY;
        ticking = false;
      });
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", function () {
      if (window.innerWidth >= 1024) header.classList.remove("is-hidden");
    });
  })();

  /* ------------------------------------------------------------------ *
   * Hero video — fade in once playback starts
   * ------------------------------------------------------------------ */

  (function heroVideo() {
    var video = qs(".hero__video");
    if (!video) return;
    video.addEventListener("playing", function () {
      video.classList.add("is-playing");
    });
    try { video.load(); } catch (e) {}
  })();

  /* ------------------------------------------------------------------ *
   * Cart — AJAX add / change / render drawer
   * ------------------------------------------------------------------ */

  var Cart = (function () {
    var drawer = qs("#CartDrawer");
    var itemsEl = drawer ? qs(".cart-drawer__items", drawer) : null;
    var emptyEl = drawer ? qs(".cart-drawer__empty", drawer) : null;
    var footerEl = drawer ? qs(".cart-drawer__footer", drawer) : null;
    var subtotalEl = drawer ? qs(".cart-drawer__subtotal-value", drawer) : null;
    var titleCountEl = drawer ? qs(".cart-drawer__count", drawer) : null;
    var countBadges = qsa(".site-header__cart-count");

    function open() {
      if (!drawer) return;
      drawer.classList.add("is-open");
      document.body.classList.add("cart-drawer-open");
      drawer.setAttribute("aria-hidden", "false");
      // Next frame so display:none → block can animate the panel in
      requestAnimationFrame(function () {
        drawer.classList.add("is-animating");
      });
    }

    function close() {
      if (!drawer) return;
      drawer.classList.remove("is-animating");
      drawer.classList.remove("is-open");
      document.body.classList.remove("cart-drawer-open");
      drawer.setAttribute("aria-hidden", "true");
    }

    function updateCountBadges(count) {
      countBadges.forEach(function (badge) {
        badge.textContent = count > 9 ? "9+" : String(count);
        badge.hidden = count <= 0;
      });
    }

    function purchaseTypeLabel(item) {
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        return item.selling_plan_allocation.selling_plan.name;
      }
      return "One-Time";
    }

    function cartItemMarkup(item) {
      var img = item.image ? item.image.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, "_160x$1$2") : "";
      var isSub = !!(item.selling_plan_allocation);
      var planLabel = purchaseTypeLabel(item);
      var lineTotal = item.final_line_price != null ? item.final_line_price : item.line_price;
      return (
        '<div class="cart-item" data-cart-item data-key="' + item.key + '">' +
          '<div class="cart-item__image">' + (img ? '<img src="' + img + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="64" height="64">' : "") + "</div>" +
          '<div class="cart-item__body">' +
            '<p class="cart-item__name">' + escapeHtml(item.product_title) + "</p>" +
            (item.variant_title ? '<p class="cart-item__variant">' + escapeHtml(item.variant_title) + "</p>" : "") +
            (isSub ? '<div class="cart-item__plan">' + iconSvg("refresh") + "<span>" + escapeHtml(planLabel) + "</span></div>" : "") +
            '<div class="cart-item__row">' +
              '<div class="qty-stepper qty-stepper--sm" data-qty-stepper data-key="' + item.key + '" data-qty="' + item.quantity + '">' +
                '<button type="button" class="qty-stepper__btn" data-qty-decrease aria-label="Decrease quantity">' + iconSvg("minus") + "</button>" +
                '<span class="qty-stepper__value" data-qty-value>' + item.quantity + "</span>" +
                '<button type="button" class="qty-stepper__btn" data-qty-increase aria-label="Increase quantity">' + iconSvg("plus") + "</button>" +
              "</div>" +
              '<span class="cart-item__price">' + formatMoney(lineTotal) + "</span>" +
            "</div>" +
          "</div>" +
          '<button type="button" class="cart-item__remove" data-cart-remove data-key="' + item.key + '" aria-label="Remove item">' + iconSvg("trash") + "</button>" +
        "</div>"
      );
    }

    function escapeHtml(str) {
      return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    function iconSvg(name) {
      var icons = {
        minus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>',
        plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
        trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6"/></svg>',
        refresh: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>'
      };
      return icons[name] || "";
    }

    function render(cart) {
      updateCountBadges(cart.item_count);
      if (!drawer) return;

      if (titleCountEl) titleCountEl.textContent = cart.item_count > 0 ? "(" + cart.item_count + ")" : "";

      if (cart.item_count === 0) {
        if (itemsEl) { itemsEl.hidden = true; itemsEl.innerHTML = ""; }
        if (footerEl) footerEl.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      if (emptyEl) emptyEl.hidden = true;
      if (itemsEl) {
        itemsEl.hidden = false;
        itemsEl.innerHTML = cart.items.map(cartItemMarkup).join("");
      }
      if (footerEl) footerEl.hidden = false;
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    function cartRoutes() {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      return {
        cart: root + "cart.js",
        add: root + "cart/add.js",
        change: root + "cart/change.js"
      };
    }

    function fetchCart() {
      return fetch(cartRoutes().cart, {
        headers: { Accept: "application/json" }
      }).then(function (r) { return r.json(); });
    }

    function refresh() {
      return fetchCart().then(function (cart) {
        render(cart);
        return cart;
      });
    }

    function addItems(items, opts) {
      opts = opts || {};
      return fetch(cartRoutes().add, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) { throw data; }
            return data;
          });
        })
        .then(function (data) {
          return refresh().then(function (cart) {
            if (!opts.silent) open();
            return { data: data, cart: cart };
          });
        });
    }

    function changeLine(key, quantity) {
      // Prefer line item key (string). Fallback: 1-based line index.
      var payload = { quantity: quantity };
      if (typeof key === "number") payload.line = key;
      else payload.id = String(key);

      return fetch(cartRoutes().change, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) {
              var msg = (data && (data.description || data.message)) || "Could not update cart";
              throw new Error(msg);
            }
            return data;
          });
        })
        .then(function (cart) {
          render(cart);
          return cart;
        });
    }

    function init() {
      if (!drawer) return;

      qsa("[data-cart-open]").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          open();
        });
      });
      qsa("[data-cart-close]", drawer).forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          close();
        });
      });
      qs(".cart-drawer__overlay", drawer) && qs(".cart-drawer__overlay", drawer).addEventListener("click", close);
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") close();
      });

      drawer.addEventListener("click", function (e) {
        var incBtn = e.target.closest("[data-qty-increase]");
        var decBtn = e.target.closest("[data-qty-decrease]");
        var removeBtn = e.target.closest("[data-cart-remove]");

        if (incBtn || decBtn) {
          var stepper = e.target.closest("[data-qty-stepper]");
          if (!stepper) return;
          var key = stepper.getAttribute("data-key");
          var current = parseInt(stepper.getAttribute("data-qty"), 10) || 1;
          var next = incBtn ? current + 1 : Math.max(0, current - 1);
          changeLine(key, next).catch(function (err) {
            console.error(err);
            showToast((err && err.message) || "Could not update cart. Please try again.");
            refresh();
          });
        }

        if (removeBtn) {
          changeLine(removeBtn.getAttribute("data-key"), 0).catch(function (err) {
            console.error(err);
            showToast((err && err.message) || "Could not remove item. Please try again.");
            refresh();
          });
        }
      });

      if (drawer.getAttribute("data-cart-count") === "0" || itemsEl && itemsEl.children.length === 0) {
        // initial server-rendered state already correct; nothing to do
      }

      qsa('a[href*="/checkout"]').forEach(function (link) {
        if (link.closest(".cart-drawer__footer")) {
          link.addEventListener("click", function () {
            fetchCart().then(function (cart) {
              fireGA4("begin_checkout", {
                currency: cart.currency,
                value: cart.total_price / 100,
                items: cart.items.map(function (item) {
                  return {
                    item_id: item.id,
                    item_name: item.product_title + " - " + item.variant_title,
                    item_category: "Turf Cleaning Products",
                    price: item.price / 100,
                    quantity: item.quantity
                  };
                })
              });
              fireOpenai("checkout_started", {
                type: "contents",
                amount: cart.total_price,
                currency: "USD",
                contents: cart.items.map(function (item) {
                  return {
                    id: String(item.id),
                    name: item.product_title + " - " + item.variant_title,
                    content_type: "product",
                    quantity: item.quantity,
                    amount: item.price,
                    currency: "USD"
                  };
                })
              });
            });
          });
        }
      });
    }

    return { open: open, close: close, refresh: refresh, addItems: addItems, changeLine: changeLine, fetchCart: fetchCart, init: init };
  })();

  window.DirtyTurfCart = Cart;
  Cart.init();

  /* ------------------------------------------------------------------ *
   * Cart page — AJAX qty updates (line index; works with selling plans)
   * ------------------------------------------------------------------ */

  (function cartPage() {
    var root = qs("[data-cart-page]");
    if (!root || !window.DirtyTurfCart) return;

    function money(cents) {
      return formatMoney(cents);
    }

    function syncFromCart(cart) {
      if (!cart.item_count) {
        window.location.reload();
        return;
      }

      var subtotalEl = qs("[data-cart-page-subtotal]", root);
      if (subtotalEl) subtotalEl.textContent = money(cart.total_price);

      // Rebuild line indexes after removals by reloading for simplicity when count changes
      var rows = qsa("[data-cart-line]", root);
      if (rows.length !== cart.items.length) {
        window.location.reload();
        return;
      }

      cart.items.forEach(function (item, index) {
        var row = rows[index];
        if (!row) return;
        row.setAttribute("data-line", String(index + 1));
        row.setAttribute("data-key", item.key);
        var stepper = qs("[data-cart-page-qty]", row);
        if (stepper) {
          stepper.setAttribute("data-line", String(index + 1));
          stepper.setAttribute("data-qty", String(item.quantity));
          var input = qs("[data-qty-value-input]", stepper);
          if (input) input.value = item.quantity;
        }
        var priceEl = qs("[data-line-price]", row);
        if (priceEl) {
          priceEl.textContent = money(
            item.final_line_price != null ? item.final_line_price : item.line_price
          );
        }
      });

      DirtyTurfCart.refresh();
    }

    function updateLine(line, quantity) {
      return DirtyTurfCart.changeLine(line, quantity)
        .then(function (cart) {
          syncFromCart(cart);
          return cart;
        })
        .catch(function (err) {
          console.error(err);
          showToast((err && err.message) || "Could not update cart. Please try again.");
          return DirtyTurfCart.fetchCart().then(syncFromCart);
        });
    }

    root.addEventListener("click", function (e) {
      var stepper = e.target.closest("[data-cart-page-qty]");
      if (stepper && (e.target.closest("[data-qty-increase]") || e.target.closest("[data-qty-decrease]"))) {
        e.preventDefault();
        var input = qs("[data-qty-value-input]", stepper);
        var line = parseInt(stepper.getAttribute("data-line"), 10);
        var min = parseInt(stepper.getAttribute("data-min") || "0", 10);
        var max = parseInt(stepper.getAttribute("data-max") || "99", 10);
        var value = parseInt(input && input.value, 10);
        if (isNaN(value)) value = parseInt(stepper.getAttribute("data-qty"), 10) || 1;

        if (e.target.closest("[data-qty-decrease]")) value = Math.max(min, value - 1);
        else value = Math.min(max, value + 1);

        if (input) input.value = value;
        stepper.setAttribute("data-qty", String(value));
        updateLine(line, value);
        return;
      }

      if (e.target.closest("[data-cart-page-update]")) {
        e.preventDefault();
        var steppers = qsa("[data-cart-page-qty]", root);
        var chain = Promise.resolve();
        steppers.forEach(function (stepper) {
          var line = parseInt(stepper.getAttribute("data-line"), 10);
          var input = qs("[data-qty-value-input]", stepper);
          var qty = parseInt(input && input.value, 10);
          if (isNaN(qty)) qty = 0;
          chain = chain.then(function () {
            return DirtyTurfCart.changeLine(line, qty);
          });
        });
        chain
          .then(function (cart) {
            // last change returns full cart
            return DirtyTurfCart.fetchCart().then(syncFromCart);
          })
          .then(function () {
            showToast("Cart updated");
          })
          .catch(function (err) {
            console.error(err);
            showToast((err && err.message) || "Could not update cart. Please try again.");
            DirtyTurfCart.fetchCart().then(syncFromCart);
          });
      }
    });

    root.addEventListener("change", function (e) {
      var input = e.target.closest("[data-cart-page-qty] [data-qty-value-input]");
      if (!input) return;
      var stepper = input.closest("[data-cart-page-qty]");
      var line = parseInt(stepper.getAttribute("data-line"), 10);
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      stepper.setAttribute("data-qty", String(qty));
      updateLine(line, qty);
    });
  })();

  /* ------------------------------------------------------------------ *
   * Generic quantity steppers (PDP only — cart page uses AJAX above)
   * ------------------------------------------------------------------ */

  (function qtySteppers() {
    document.addEventListener("click", function (e) {
      var stepper = e.target.closest("[data-qty-stepper-standalone]");
      if (!stepper) return;
      // Cart page has its own handler
      if (stepper.hasAttribute("data-cart-page-qty") || stepper.closest("[data-cart-page]")) return;
      var input = qs("[data-qty-value-input]", stepper);
      var min = parseInt(stepper.getAttribute("data-min") || "1", 10);
      var max = parseInt(stepper.getAttribute("data-max") || "12", 10);
      var value = parseInt(input.value, 10) || min;

      if (e.target.closest("[data-qty-decrease]")) {
        value = Math.max(min, value - 1);
      } else if (e.target.closest("[data-qty-increase]")) {
        value = Math.min(max, value + 1);
      } else {
        return;
      }
      input.value = value;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  })();

  /* ------------------------------------------------------------------ *
   * Accordion (FAQ)
   * ------------------------------------------------------------------ */

  document.addEventListener("click", function (e) {
    var trigger = e.target.closest(".accordion-trigger");
    if (!trigger) return;
    var item = trigger.closest(".accordion-item");
    var accordion = trigger.closest(".accordion");
    var wasOpen = item.classList.contains("is-open");

    if (accordion && accordion.getAttribute("data-accordion-type") === "single") {
      qsa(".accordion-item.is-open", accordion).forEach(function (openItem) {
        if (openItem !== item) openItem.classList.remove("is-open");
      });
    }
    item.classList.toggle("is-open", !wasOpen);
  });

  /* ------------------------------------------------------------------ *
   * Modal (SDS dialog, etc.)
   * ------------------------------------------------------------------ */

  document.addEventListener("click", function (e) {
    var opener = e.target.closest("[data-modal-open]");
    if (opener) {
      var modal = qs("#" + opener.getAttribute("data-modal-open"));
      if (modal) modal.classList.add("is-open");
    }
    var closer = e.target.closest("[data-modal-close]");
    if (closer) {
      var modalToClose = closer.closest(".modal");
      if (modalToClose) modalToClose.classList.remove("is-open");
    }
  });

  /* ------------------------------------------------------------------ *
   * Product form — variant + selling plan + qty + add to cart
   * ------------------------------------------------------------------ */

  function ProductForm(root) {
    var dataEl = qs("script[data-product-json]", root);
    if (!dataEl) return;

    var product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error("[DirtyTurf] Invalid product JSON", err);
      return;
    }
    if (!product || !product.variants || !product.variants.length) return;

    function normalizePlanId(id) {
      if (id == null || id === "") return null;
      var match = String(id).match(/(\d+)$/);
      return match ? match[1] : String(id);
    }

    var state = {
      options: (product.selected_options || product.variants[0].options).slice(),
      sellingPlanId: null,
      quantity: 1
    };

    var variantIdInput = qs("[data-variant-id]", root);
    var addBtn = qs("[data-add-to-cart]", root);
    var addBtnLabel = addBtn ? qs("[data-add-label]", addBtn) : null;
    var qtyInput = qs("[data-qty-value-input]", root);
    var priceCurrent = qs("[data-price-current]", root);
    var priceCompare = qs("[data-price-compare]", root);
    var savingsEl = qs("[data-savings]", root);
    var perIntervalEl = qs("[data-per-interval]", root);
    var qtyTotalEl = qs("[data-qty-total]", root);
    var galleryImg = qs("[data-gallery-image]", root);
    var purchaseFlow = qs("[data-purchase-flow]", root);
    var bulkCta = qs("[data-bulk-cta]", root);
    var sellingPlanWrap = qs("[data-selling-plan-options]", root);

    var sellingPlanInput = qs("[data-selling-plan-input]", root);

    function findVariant() {
      return product.variants.filter(function (v) {
        return v.options.every(function (opt, i) {
          return state.options[i] == null || String(opt) === String(state.options[i]);
        });
      })[0];
    }

    function updateOptionButtons() {
      qsa("button[data-option-index]", root).forEach(function (btn) {
        var idx = parseInt(btn.getAttribute("data-option-index"), 10);
        var val = btn.getAttribute("data-option-value");
        btn.classList.toggle("is-active", String(state.options[idx]) === String(val));
      });
    }

    function sellingPlanAllocationFor(variant, planId) {
      if (!variant.selling_plan_allocations) return null;
      var normalized = normalizePlanId(planId);
      return variant.selling_plan_allocations.filter(function (a) {
        return normalizePlanId(a.selling_plan_id) === normalized;
      })[0] || null;
    }

    function renderSellingPlans(variant) {
      if (!sellingPlanWrap) return;
      var allocations = variant.selling_plan_allocations || [];

      if (allocations.length === 0) {
        sellingPlanWrap.innerHTML = "";
        sellingPlanWrap.hidden = true;
        state.sellingPlanId = null;
        return;
      }

      sellingPlanWrap.hidden = false;
      var buttons = ['<button type="button" class="pdp__purchase-option' + (state.sellingPlanId === null ? " is-active" : "") + '" data-selling-plan-btn data-plan-id="">' +
        '<span class="pdp__purchase-option-label">One-Time Purchase</span>' +
        '<span class="pdp__purchase-option-price">' + formatMoney(variant.price) + "</span></button>"];

      allocations.forEach(function (alloc) {
        var savingPct = Math.max(0, Math.round(((variant.price - alloc.price) / variant.price) * 100));
        var planId = normalizePlanId(alloc.selling_plan_id);
        var isActive = state.sellingPlanId !== null && state.sellingPlanId === planId;
        buttons.push(
          '<button type="button" class="pdp__purchase-option' + (isActive ? " is-active" : "") + '" data-selling-plan-btn data-plan-id="' + planId + '">' +
            '<span class="pdp__purchase-option-label">' + escapeHtml(alloc.selling_plan_name) +
            (savingPct > 0 ? ' <span class="badge badge--orange">Save ' + savingPct + '%</span>' : "") + "</span>" +
            '<span class="pdp__purchase-option-price">' + formatMoney(alloc.price) + "</span></button>"
        );
      });

      sellingPlanWrap.innerHTML = buttons.join("");
    }

    function escapeHtml(str) {
      return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    function isBulk(variant) {
      // Wholesale-only sizes: drums/totes plus Enzyme Prime case/pail packs
      return /\b(drum|tote|case|pail)\b/i.test(variant.title);
    }

    function updatePrice(variant) {
      var alloc = state.sellingPlanId ? sellingPlanAllocationFor(variant, state.sellingPlanId) : null;
      var current = alloc ? alloc.price : variant.price;
      var showCompare = alloc && alloc.price < variant.price;

      if (priceCurrent) priceCurrent.textContent = formatMoney(current);
      if (priceCompare) {
        priceCompare.hidden = !showCompare;
        priceCompare.textContent = showCompare ? formatMoney(variant.price) : "";
      }
      if (savingsEl) {
        if (showCompare) {
          var pct = Math.round(((variant.price - current) / variant.price) * 100);
          savingsEl.hidden = false;
          savingsEl.textContent = "You save " + pct + "%";
        } else {
          savingsEl.hidden = true;
        }
      }
      if (perIntervalEl) {
        if (alloc && alloc.selling_plan_interval) {
          perIntervalEl.hidden = false;
          var intervalSpan = qs("span", perIntervalEl);
          if (intervalSpan) intervalSpan.textContent = "per " + alloc.selling_plan_interval;
        } else {
          perIntervalEl.hidden = true;
        }
      }
      if (qtyTotalEl) {
        if (state.quantity > 1) {
          qtyTotalEl.hidden = false;
          qtyTotalEl.textContent = "= " + formatMoney(current * state.quantity);
        } else {
          qtyTotalEl.hidden = true;
        }
      }
      return current;
    }

    function updateForVariant() {
      updateOptionButtons();

      var variant = findVariant();
      if (!variant) return;

      if (variantIdInput) variantIdInput.value = variant.id;
      if (sellingPlanInput) {
        sellingPlanInput.value = state.sellingPlanId || "";
        sellingPlanInput.disabled = !state.sellingPlanId;
      }
      renderSellingPlans(variant);
      updatePrice(variant);

      if (galleryImg && variant.featured_image) {
        galleryImg.src = variant.featured_image;
      }

      var bulk = isBulk(variant);
      if (bulkCta) bulkCta.hidden = !bulk;
      if (purchaseFlow) purchaseFlow.hidden = bulk;

      if (addBtn) addBtn.disabled = !variant.available;
      if (addBtnLabel) {
        addBtnLabel.textContent = !variant.available
          ? "Sold Out"
          : state.sellingPlanId
            ? "Add Subscription to Cart"
            : "Add to Cart";
      }

      root.dispatchEvent(new CustomEvent("variant:change", { detail: { variant: variant } }));
      return variant;
    }

    // Capture-phase so option clicks always reach us even if something stops bubbling later
    root.addEventListener("click", function (e) {
      var optBtn = e.target.closest("button[data-option-index]");
      if (optBtn && root.contains(optBtn)) {
        e.preventDefault();
        e.stopPropagation();
        var idx = parseInt(optBtn.getAttribute("data-option-index"), 10);
        if (Number.isNaN(idx)) return;
        state.options[idx] = optBtn.getAttribute("data-option-value");
        state.sellingPlanId = null;
        state.quantity = 1;
        if (qtyInput) qtyInput.value = 1;
        updateForVariant();
        return;
      }

      var planBtn = e.target.closest("[data-selling-plan-btn]");
      if (planBtn && root.contains(planBtn)) {
        e.preventDefault();
        e.stopPropagation();
        state.sellingPlanId = normalizePlanId(planBtn.getAttribute("data-plan-id"));
        updateForVariant();
        return;
      }

      var cartBtn = e.target.closest("[data-add-to-cart]");
      if (cartBtn && root.contains(cartBtn)) {
        e.preventDefault();
        e.stopPropagation();
        var variant = findVariant();
        if (!variant || !variant.available || cartBtn.disabled) return;

        var item = { id: variant.id, quantity: state.quantity };
        if (state.sellingPlanId) item.selling_plan = parseInt(state.sellingPlanId, 10);

        cartBtn.disabled = true;
        var originalLabel = addBtnLabel ? addBtnLabel.textContent : "";

        Cart.addItems([item])
          .then(function () {
            if (addBtnLabel) {
              cartBtn.classList.add("btn--success");
              addBtnLabel.textContent = "Added to Cart!";
            }
            showToast(product.title + " added to cart");

            var price = updatePrice(variant);
            fireGA4("add_to_cart", {
              currency: "USD",
              value: (price / 100) * state.quantity,
              items: [{
                item_id: variant.id,
                item_name: product.title + " - " + variant.title,
                item_category: product.type,
                price: price / 100,
                quantity: state.quantity
              }]
            });
            fireOpenai("items_added", {
              type: "contents",
              amount: price * state.quantity,
              currency: "USD",
              contents: [{
                id: String(variant.id),
                name: product.title + " - " + variant.title,
                content_type: "product",
                quantity: state.quantity,
                amount: price,
                currency: "USD"
              }]
            });

            setTimeout(function () {
              cartBtn.classList.remove("btn--success");
              if (addBtnLabel) addBtnLabel.textContent = originalLabel;
              cartBtn.disabled = false;
            }, 2000);
          })
          .catch(function (err) {
            console.error("[DirtyTurf] add to cart failed", err);
            cartBtn.disabled = false;
            showToast("Something went wrong. Please try again.");
          });
      }
    }, true);

    if (qtyInput) {
      qtyInput.addEventListener("change", function () {
        state.quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        updateForVariant();
      });
    }

    root.setAttribute("data-product-form-ready", "true");

    var initial = updateForVariant();
    if (initial) {
      fireGA4("view_item", {
        currency: "USD",
        value: initial.price / 100,
        items: [{
          item_id: initial.id,
          item_name: product.title + " - " + initial.title,
          item_category: product.type,
          price: initial.price / 100,
          quantity: 1
        }]
      });
      fireOpenai("contents_viewed", {
        type: "contents",
        contents: [{
          id: String(initial.id),
          name: product.title + " - " + initial.title,
          content_type: "product",
          amount: initial.price,
          currency: "USD"
        }]
      });
    }
  }

  function initProductForms() {
    qsa("[data-product-form]").forEach(function (root) {
      if (root.getAttribute("data-product-form-ready") === "true") return;
      try {
        ProductForm(root);
      } catch (err) {
        console.error("[DirtyTurf] ProductForm init failed", err);
      }
    });
  }

  initProductForms();
  document.addEventListener("DOMContentLoaded", initProductForms);
  // Shopify section re-renders (theme editor / apps)
  document.addEventListener("shopify:section:load", initProductForms);

  /* ------------------------------------------------------------------ *
   * Product reviews — show more
   * ------------------------------------------------------------------ */

  (function productReviews() {
    qsa("[data-product-reviews]").forEach(function (section) {
      var pageSize = parseInt(section.getAttribute("data-page-size") || "6", 10);
      var cards = qsa("[data-review-card]", section);
      var moreBtn = qs("[data-reviews-more]", section);
      var remainingEl = qs("[data-reviews-remaining]", section);
      if (!moreBtn) return;

      var shown = pageSize;
      moreBtn.addEventListener("click", function () {
        shown = Math.min(cards.length, shown + pageSize);
        cards.forEach(function (card, i) {
          if (i < shown) {
            card.hidden = false;
            card.classList.remove("is-collapsed");
          }
        });
        var left = cards.length - shown;
        if (remainingEl) remainingEl.textContent = String(left);
        if (left <= 0) moreBtn.hidden = true;
        else moreBtn.innerHTML = "Show More Reviews (" + left + " remaining)";
      });
    });
  })();

  /* ------------------------------------------------------------------ *
   * View cart event when drawer opens with items
   * ------------------------------------------------------------------ */

  (function viewCartTracking() {
    qsa("[data-cart-open]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        Cart.fetchCart().then(function (cart) {
          if (cart.item_count === 0) return;
          fireGA4("view_cart", {
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map(function (item) {
              return {
                item_id: item.id,
                item_name: item.product_title + " - " + item.variant_title,
                item_category: "Turf Cleaning Products",
                price: item.price / 100,
                quantity: item.quantity
              };
            })
          });
        });
      });
    });
  })();
})();
